import { Keyboard, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { HeadlineSlider } from '@/components/shared/einstein/headline-slider';
import {
  EinsteinLineupItem,
  LCVLineupCard,
} from '@/components/shared/einstein/lineup/card';
import { PaginationBar } from '@/components/shared/einstein/pagination-bar';
import { Container } from '@/components/shared/layout/container';
import { Section } from '@/components/shared/layout/section';
import { removeHash } from '@/components/shared/utility/format';
import type { IHeadline, IMy24ProductMenu } from '@/types/shared';
import { ITracking } from '@/types/tracking';

export interface ProductLineupProps {
  active?: boolean;
  menu?: IMy24ProductMenu;
  headline?: IHeadline;
  items?: EinsteinLineupItem[];
  dataTrack?: ITracking;
}

export const LCVProductLineup = ({
  headline = {
    preTitle: 'รุ่นรถอีซูซุ',
    title: 'MODEL LINEUP',
  },
  menu,
  items = [],
  dataTrack,
}: ProductLineupProps) => {
  const hash = menu?.hash || '';
  return (
    <>
      {items?.length > 0 && (
        <Section
          data-hash={hash}
          size="2xl"
          test="section_product_lineup"
          data-impression="product-lineup-carousel"
          css={{
            background:
              'linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), linear-gradient(180deg, #2B2B2B 0%, #1A1A1A 100%)',
          }}
        >
          <Container
            css={{
              position: 'relative',
              '& .swiper': {
                overflow: 'initial',
              },
              '& .swiper-slide': {
                width: '312px',
                '@lg': {
                  width: '352px',
                },
              },
            }}
          >
            {!!headline?.title && (
              <HeadlineSlider
                title={headline?.title}
                preTitle={headline?.preTitle}
                sectionName={removeHash(hash)}
                prevButtonClass={`button-${removeHash(hash)}-prev`}
                nextButtonClass={`button-${removeHash(hash)}-next`}
              />
            )}
            <Swiper
              modules={[Pagination, Keyboard, Navigation]}
              keyboard
              speed={1000}
              threshold={10}
              slidesPerView="auto"
              spaceBetween={12}
              breakpoints={{
                '1024': {
                  spaceBetween: 24,
                },
              }}
              pagination={{
                el: '.swiper-pagination-product-lineup',
                clickable: true,
                renderBullet: function (index, className) {
                  return `<div class="${className}" data-test="pagination_${
                    index + 1
                  }"><span>${index + 1}</span>
                  </div>`;
                },
              }}
              navigation={{
                prevEl: `.button-${removeHash(hash)}-prev`,
                nextEl: `.button-${removeHash(hash)}-next`,
              }}
              data-test="product_lineup_items"
            >
              {items?.map((item, i) => (
                <SwiperSlide key={i} data-test={`product_lineup_item_${i + 1}`}>
                  <LCVLineupCard
                    item={item}
                    dataTrack={dataTrack}
                    isInView
                    showBg
                  />
                </SwiperSlide>
              ))}
            </Swiper>

            {items?.length > 1 && (
              <PaginationBar
                variant="white"
                justify="center"
                isStatic
                css={{
                  marginTop: '$space-8',
                  '@lg': {
                    marginTop: '$space-10',
                  },
                }}
              >
                <div
                  className="swiper-pagination swiper-pagination-product-lineup"
                  data-test="paginations"
                />
              </PaginationBar>
            )}
          </Container>
        </Section>
      )}
    </>
  );
};

LCVProductLineup.displayName = 'SectionProductLineUp';
